import React, { useEffect, useRef, useState, useMemo } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  Button,
  Typography,
  Breadcrumb,
  Space,
  Spin,
  Modal,
  Input,
  Row,
  Col,
  notification,
  Result,
  DatePicker,
  Radio,
  Form,
} from "antd";
import { UserOutlined, Loading3QuartersOutlined, FormOutlined } from "@ant-design/icons";

//
import log from "../../../utils/log";
import c from "../../../utils/common";
import moment from "moment-timezone";
import metaData from "../../../data/metaData";
import _ from "lodash";

//
import RequestDetailView from "../../../components/ShopRequest/View";
import RequestMeta from "../../../components/RequestMeta";

//
import useAuthStore from "../../../states/useAuth";
import useRequest from "../../../hooks/useRequest";
import PageHeader from "../../../components/PageHeader";
import Status from "../../../components/Table/RequestStatus";
import useRequestStore from "../../../states/useRequest";
import deepcopy from "deepcopy";
import { showErrMsg } from "../../../utils/error";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const SingleShopRequest = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams(); // Get the :id parameter from the URL
  const { resetRequest } = useRequestStore();
  const { getSingleShopRequest, updateShopRequest } = useRequest();

  //Sider
  const [collapsed, setCollapsed] = useState(false);

  const [shopForm] = Form.useForm();
  const [brandForm] = Form.useForm();
  const [companyForm] = Form.useForm();

  const [isShowRemarkModal, setIsShowRemarkModal] = useState(false);
  const [isShowApproveModal, setIsShowApproveModal] = useState(false);
  const [request, setRequest] = useState();
  const [isFetchingSingleRequest, setIsFetchingSingleRequest] = useState(false);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [isShowErrorPage, setIsShowErrorPage] = useState(false);

  const makeDecision = async ({ action, remark, permission }) => {
    log.debug({ action, remark, permission });

    // axios
    try {
      setIsLoadingUpdate(true);
      let updated = await updateShopRequest(request.id, { status: action, remark, permission }, false);
      setIsLoadingUpdate(false);
      log.debug(updated);

      //update data
      setRequest({ ...request, approverId: updated.approverId, time: updated.time, status: updated.status });

      //
      notification.success({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Success
          </Title>
        ),
        // description: <Text strong></Text>,
      });
    } catch (error) {
      showErrMsg(error);
    }
  };

  const getSingleRequestData = async () => {
    setIsFetchingSingleRequest(true);

    //setActiveTab
    let queryURL = location.search || undefined;
    log.debug(queryURL);
    // const queryParams = c.parseQueryToJSON(queryURL);
    // const { tab } = queryParams;
    // log.debug(tab);
    // setActiveTab(tab);

    //
    let result;

    try {
      result = await getSingleShopRequest(id, false);
      log.debug(result);

      setRequest(result);
    } catch (error) {
      setIsShowErrorPage(true);
      notification.error({
        message: (
          <Title style={{ margin: 0 }} level={4}>
            Error
          </Title>
        ),
        description: <Text strong>Please refresh this page</Text>,
      });
    } finally {
      setIsFetchingSingleRequest(false);
    }
  };

  // Function to handle updating both the form and state
  const updateRequestData = (newData) => {
    //do nothing
  };

  const registerOnRefreshEvent = () => {
    const handleBeforeUnload = () => {
      resetRequest(); // Reset the state when the page is refreshed or closed
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload); // Properly remove the listener
    };
  };

  useEffect(() => {
    return registerOnRefreshEvent(); // Cleanup when component unmounts
  }, []);

  useEffect(() => {
    getSingleRequestData();
  }, [id]);

  return (
    <div
      style={{
        margin: "0 auto",
        // background: "var(--ion-color-step-50)",
      }}
    >
      <Spin
        spinning={isFetchingSingleRequest}
        // percent="auto"
        indicator={<Loading3QuartersOutlined spin style={{ color: "white", fontSize: "2rem" }} />}
        fullscreen
      />

      {isShowErrorPage ? (
        <Result
          status="404"
          title="404"
          subTitle="Sorry, the page you visited does not exist."
          extra={
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              Refresh
            </Button>
          }
        />
      ) : (
        <>
          <Breadcrumb
            items={[
              {
                title: (
                  <a
                    onClick={() => {
                      history.push("/approver/shop/request");
                    }}
                  >
                    Shop List
                  </a>
                ),
              },
              {
                title: <a>Shop</a>,
              },
            ]}
          />

          <PageHeader
            title={<Space>Request Detail {request ? <Status status={request.status} /> : null}</Space>}
            isSticky
            actionGroup={
              <Space>
                {request?.status && request?.status != "approved" ? (
                  <Button
                    size="large"
                    color="danger"
                    variant="solid"
                    loading={isLoadingUpdate}
                    style={{ marginRight: "8px" }}
                    onClick={async () => {
                      // let data = await parseFormData();
                      setIsShowRemarkModal(true);
                    }}
                  >
                    <FormOutlined />
                    Needs Revision
                  </Button>
                ) : null}

                {request?.status && request?.status == "pending" ? (
                  <Button
                    size="large"
                    type="primary"
                    loading={isLoadingUpdate}
                    style={{ marginRight: "8px" }}
                    onClick={async () => {
                      setIsShowApproveModal(true);
                    }}
                  >
                    Approve
                  </Button>
                ) : null}
              </Space>
            }
            onBack={() => {
              history.goBack();
            }}
          />

          <RevisionModal
            isShowRemarkModal={isShowRemarkModal}
            onCancel={() => {
              setIsShowRemarkModal(false);
            }}
            onSubmit={(value) => {
              makeDecision({ action: "needRevision", remark: value });
              setIsShowRemarkModal(false);
            }}
          />

          <ApproveModal
            isShowApproveModal={isShowApproveModal}
            companyPermission={request?.user?.company?.permission}
            onCancel={() => {
              setIsShowApproveModal(false);
            }}
            onSubmit={(value) => {
              makeDecision({ action: "approved", permission: value });
              setIsShowApproveModal(false);
            }}
          />

          {isFetchingSingleRequest ? null : (
            <>
              {request ? <RequestMeta request={request} /> : null}

              <Row
                style={{
                  background: "var(--ion-color-step-50)",
                  paddingTop: "2rem",
                }}
              >
                <RequestDetailView
                  state={{
                    data: request,
                    onChange: updateRequestData,
                  }}
                  shopForm={shopForm}
                  brandForm={brandForm}
                  companyForm={companyForm}
                />
              </Row>
            </>
          )}
        </>
      )}
    </div>
  );
};

const TakeawayToggle = ({ state = {}, readOnly, ...props }) => {
  const { data, form, rules, onChange } = state;

  // Sync Ant Design form with state
  useEffect(() => {
    form?.setFieldsValue({ isTakeawayEnabled: data });
  }, [data]);

  return (
    <Form form={form} style={{ width: "100%" }}>
      <Form.Item label="Enable Takeaway Feature" name="isTakeawayEnabled" rules={rules} required={false}>
        <Radio.Group
          onChange={(e) => onChange({ isTakeawayEnabled: e?.target?.value })}
          value={data}
          disabled={readOnly}
          {...props}
        >
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </Form.Item>
    </Form>
  );
};

const ApproveModal = ({ isShowApproveModal, companyPermission, onCancel, onSubmit }) => {
  const [plan, setPlan] = useState();
  const [isTakeawayEnabled, setIsTakeawayEnabled] = useState(false);
  const [productNum, setProductNum] = useState();
  const [sellNum, setSellNum] = useState();
  const [promotionCodeNum, setPromotionCodeNum] = useState();
  const [expiry, setExpiry] = useState();
  const [form] = Form.useForm();

  // Clear form function only called when canceling or after successful submission
  const clearForm = () => {
    setPlan();
    setIsTakeawayEnabled(false);
    setProductNum();
    setSellNum();
    setPromotionCodeNum();
    setExpiry();
    form.resetFields(); // Reset form fields
  };

  return (
    <Modal
      title="Before Approve"
      open={isShowApproveModal}
      onCancel={() => {
        clearForm(); // Clear when modal is canceled
        onCancel();
      }}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            clearForm(); // Clear when user clicks Cancel
            onCancel();
          }}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={async () => {
            try {
              // Validate the form fields first
              await form.validateFields();
            } catch (error) {
              console.debug(error);
              return; // Stop submission if form validation fails
            }

            // Collect and parse data before submitting
            const data = {
              plan,
              isTakeawayEnabled,
              quota: {
                productNum: parseInt(productNum, 10),
                sellNum: parseInt(sellNum, 10),
                promotionCodeNum: parseInt(promotionCodeNum, 10),
              },
              expiry: expiry ? expiry.toISOString() : null,
            };

            onSubmit(data); // Submit the form data
            clearForm(); // Clear form after successful submission
          }}
        >
          Submit
        </Button>,
      ]}
    >
      <Text type="secondary">Fill in billing info before approval</Text>

      <Form form={form} layout="vertical" style={{ marginTop: "1rem" }}>
        {/* Plan */}
        {companyPermission?.plan ? (
          <Row style={{ width: "100%", marginBottom: "1rem" }}>
            <Text>
              Plan: <Text strong>{companyPermission?.plan}</Text>
            </Text>
          </Row>
        ) : (
          <Form.Item
            label={<span>Plan</span>}
            name="plan"
            rules={[{ required: true, message: "required" }]}
            required={false}
          >
            <Radio.Group onChange={(e) => setPlan(e.target.value)} value={plan}>
              <Radio value="basic">Basic</Radio>
              <Radio value="premium">Premium</Radio>
            </Radio.Group>
          </Form.Item>
        )}

        {/* Enable Takeaway Feature */}
        {companyPermission?.isTakeawayEnabled != undefined ? (
          <Row style={{ width: "100%", marginBottom: "1rem" }}>
            <Text>
              Enable Takeaway Feature: <Text strong>{companyPermission?.isTakeawayEnabled ? "Yes" : "No"}</Text>
            </Text>
          </Row>
        ) : (
          <TakeawayToggle
            state={{
              data: isTakeawayEnabled,
              form: form,
              onChange: (value) => setIsTakeawayEnabled(value),
              rules: [{ required: true, message: "required" }],
            }}
          />
        )}

        {/* Quota */}
        {companyPermission?.quota ? (
          <Row gutter={16} style={{ width: "100%", margin: "1rem 0" }}>
            <Col span={12}>
              <Text>
                Product Quota : <Text strong>{companyPermission?.quota?.productNum}</Text>
              </Text>
            </Col>
            <Col span={12}>
              <Text>
                Sale Quota : <Text strong>{companyPermission?.quota?.sellNum}</Text>
              </Text>
            </Col>
            <Col span={12}>
              <Text>
                Promotion Code Quota : <Text strong>{companyPermission?.quota?.promotionCodeNum}</Text>
              </Text>
            </Col>
          </Row>
        ) : (
          <Row gutter={16} style={{marginTop: "3rem"}}>
            <Col span={12}>
              <Form.Item
                label={<span>Product Quota</span>}
                name="productNum"
                rules={[{ required: true, message: "required" }]}
                required={false}
              >
                <Input
                  type="number"
                  min={0}
                  value={productNum}
                  onChange={(e) => setProductNum(e.target.value)}
                  placeholder="Enter Integer"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={<span>Sell Quota</span>}
                name="sellNum"
                rules={[{ required: true, message: "required" }]}
                required={false}
              >
                <Input
                  type="number"
                  min={0}
                  value={sellNum}
                  onChange={(e) => setSellNum(e.target.value)}
                  placeholder="Enter Integer"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={<span>Promotion Code Quota</span>}
                name="promotionCodeNum"
                rules={[{ required: true, message: "required" }]}
                required={false}
              >
                <Input
                  type="number"
                  min={0}
                  value={promotionCodeNum}
                  onChange={(e) => setPromotionCodeNum(e.target.value)}
                  placeholder="Enter Integer"
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Expiry Date */}
        <Form.Item
          label={<span>Expiry Date for this shop</span>}
          name="expiry"
          rules={[{ required: true, message: "required" }]}
          required={false}
        >
          {/* {JSON.stringify(moment().toISOString())} */}
          <DatePicker
            value={expiry}
            onChange={(date) => setExpiry(date)}
            placeholder="Select expiry date"
            style={{ width: "100%" }}
            disabledDate={(current) => {
              // Disable all dates before today and today itself
              return current && current < moment();
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const RevisionModal = ({ isShowRemarkModal, onCancel, onSubmit }) => {
  const [revisionRemark, setRevisionRemark] = useState();
  return (
    <Modal
      title="Request Revision"
      open={isShowRemarkModal}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          onClick={() => {
            let remark = deepcopy(revisionRemark);
            remark = remark || undefined;
            setRevisionRemark();
            onSubmit(remark);
          }}
        >
          Submit
        </Button>,
      ]}
    >
      <TextArea
        rows={4}
        value={revisionRemark}
        onChange={(e) => setRevisionRemark(e.target.value)}
        placeholder="Enter your revision remarks here"
      />
    </Modal>
  );
};

export default SingleShopRequest;
